import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileText, Clock, CheckCircle, XCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { useReportGenerationTasksQuery, type ReportGenerationTask } from '@/lib/api-extensions/admin-queries';
import { cn } from '@/lib/utils';
import { formatDistance } from 'date-fns';

interface ReportGenerationStatusProps {
  className?: string;
  compact?: boolean;
}

export function ReportGenerationStatus({ className, compact = false }: ReportGenerationStatusProps) {
  const [statusFilter, setStatusFilter] = useState<'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'ALL'>('ALL');
  const [reportTypeFilter, setReportTypeFilter] = useState<
    'WEEKLY_REPORT_GENERATION' | 'STRATEGIC_REPORT_GENERATION' | 'ALL'
  >('ALL');
  const [selectedError, setSelectedError] = useState<{ taskId: number; message: string } | null>(null);

  const { data, isLoading, error } = useReportGenerationTasksQuery(
    statusFilter === 'ALL' ? undefined : statusFilter,
    reportTypeFilter === 'ALL' ? undefined : reportTypeFilter,
  );

  const getStatusBadge = (state: ReportGenerationTask['state']) => {
    switch (state) {
      case 'IN_PROGRESS':
        return (
          <Badge variant="default" className="bg-blue-500 hover:bg-blue-600">
            <Clock className="mr-1 h-3 w-3" />
            In Progress
          </Badge>
        );
      case 'COMPLETED':
        return (
          <Badge variant="default" className="bg-green-500 hover:bg-green-600">
            <CheckCircle className="mr-1 h-3 w-3" />
            Completed
          </Badge>
        );
      case 'FAILED':
        return (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" />
            Failed
          </Badge>
        );
    }
  };

  const getCompactStatusIcon = (state: ReportGenerationTask['state']) => {
    switch (state) {
      case 'IN_PROGRESS':
        return <Clock className="h-4 w-4 text-blue-500 animate-pulse" />;
      case 'COMPLETED':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'FAILED':
        return <XCircle className="h-4 w-4 text-red-500" />;
    }
  };

  const getReportTypeLabel = (taskType: ReportGenerationTask['task_type']) => {
    switch (taskType) {
      case 'WEEKLY_REPORT_GENERATION':
        return 'Weekly Report';
      case 'STRATEGIC_REPORT_GENERATION':
        return 'Strategic Report';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return formatDistance(date, new Date(), { addSuffix: true });
  };

  if (compact) {
    return (
      <div className={cn("w-full space-y-4", className)}>
        {/* Compact Header */}
        <div className="flex items-center justify-between px-1">
          <h3 className="font-semibold text-sm text-slate-900">Recent Tasks</h3>
          {data?.has_active_tasks && (
            <RefreshCw className="h-3 w-3 animate-spin text-slate-400" />
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="text-xs text-red-500 px-1">
            Failed to load tasks
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center py-4">
            <RefreshCw className="h-4 w-4 animate-spin text-slate-300" />
          </div>
        )}

        {/* Compact List */}
        {!isLoading && !error && data && (
          <div className="space-y-2">
            {data.tasks.length === 0 ? (
              <div className="text-xs text-slate-500 px-1 italic">No recent tasks</div>
            ) : (
              data.tasks.slice(0, 10).map((task) => (
                <div 
                  key={task.id} 
                  className="group flex items-start gap-3 p-2 rounded-lg hover:bg-slate-100 transition-colors border border-transparent hover:border-slate-200"
                >
                  <div className="mt-0.5 flex-shrink-0">
                    {getCompactStatusIcon(task.state)}
                  </div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {getReportTypeLabel(task.task_type)}
                      </p>
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>{formatTimestamp(task.updated_at || task.created_at)}</span>
                    </div>
                    {task.error_message && (
                      <button
                        onClick={() => setSelectedError({ taskId: task.id, message: task.error_message! })}
                        className="text-xs text-red-600 hover:underline flex items-center gap-1 mt-1"
                      >
                        <AlertCircle className="h-3 w-3" />
                        View Error
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Error Details Dialog (Shared) */}
        <Dialog open={!!selectedError} onOpenChange={(open) => !open && setSelectedError(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <XCircle className="h-5 w-5" />
                Error Details - Task #{selectedError?.taskId}
              </DialogTitle>
              <DialogDescription>Full error message from the failed task</DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              <div className="rounded-md bg-destructive/10 p-4 border border-destructive/20">
                <pre className="text-sm whitespace-pre-wrap break-words font-mono text-destructive">
                  {selectedError?.message}
                </pre>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <Card className={cn("w-full max-w-2xl", className)}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Report Generation Status
            </CardTitle>
            <CardDescription>Monitor report generation tasks in progress and recently completed</CardDescription>
          </div>
          {data?.has_active_tasks && (
            <Badge variant="outline" className="gap-1">
              <RefreshCw className="h-3 w-3 animate-spin" />
              Auto-refreshing
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="mb-4 flex gap-4">
          <div className="w-48">
            <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as typeof statusFilter)}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Statuses</SelectItem>
                <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="FAILED">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-48">
            <Select value={reportTypeFilter} onValueChange={(value) => setReportTypeFilter(value as typeof reportTypeFilter)}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by report type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Report Types</SelectItem>
                <SelectItem value="WEEKLY_REPORT_GENERATION">Weekly Reports</SelectItem>
                <SelectItem value="STRATEGIC_REPORT_GENERATION">Strategic Reports</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error instanceof Error ? error.message : 'Failed to load tasks'}</AlertDescription>
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Tasks Table */}
        {!isLoading && !error && data && (
          <>
            {data.tasks.length === 0 ? (
              <Alert>
                <AlertDescription>No report generation tasks found</AlertDescription>
              </Alert>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="px-4 py-2 text-left text-sm font-medium text-muted-foreground">Status</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-muted-foreground">Report Type</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-muted-foreground">Created</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-muted-foreground">Updated</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-muted-foreground">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tasks.map((task) => (
                      <tr key={task.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3">{getStatusBadge(task.state)}</td>
                        <td className="px-4 py-3 text-sm">{getReportTypeLabel(task.task_type)}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {formatTimestamp(task.created_at)}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {task.updated_at ? formatTimestamp(task.updated_at) : '-'}
                        </td>
                        <td className="px-4 py-3">
                          {task.error_message ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 gap-1 text-destructive hover:text-destructive"
                              onClick={() => setSelectedError({ taskId: task.id, message: task.error_message! })}
                            >
                              <AlertCircle className="h-3 w-3" />
                              View Error
                            </Button>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Summary */}
            <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
              <div>Total tasks: {data.total_count}</div>
              {data.has_active_tasks && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Active tasks are being monitored
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>

      {/* Error Details Dialog */}
      <Dialog open={!!selectedError} onOpenChange={(open) => !open && setSelectedError(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <XCircle className="h-5 w-5" />
              Error Details - Task #{selectedError?.taskId}
            </DialogTitle>
            <DialogDescription>Full error message from the failed task</DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <div className="rounded-md bg-destructive/10 p-4 border border-destructive/20">
              <pre className="text-sm whitespace-pre-wrap break-words font-mono text-destructive">
                {selectedError?.message}
              </pre>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
